'use client';

import { useMemo } from 'react';
import { useSubscription } from './queries/useBilling';
import type { SubscriptionWithPayments } from '../lib/api';

const DAY_MS = 1000 * 60 * 60 * 24;

// 체험 배너는 TRIALING(만료 임박)과 EXPIRED(만료 후) 두 상태에서만 노출한다.
// CANCELLED / PAST_DUE는 체험이 아니라 유료 구독 이탈 축이라 제외한다.
// PRO / FREE 두 분기가 같은 판정을 써야 하므로(한쪽만 고치면 티어에 따라 동작이 갈린다) 여기로 모은다.
const getTrialExposure = (
  subscription: SubscriptionWithPayments | null | undefined,
  billingEnabled: boolean,
): { trialDaysLeft: number | null; showTrialBanner: boolean } => {
  const status = subscription?.status;
  const trialEndAt = subscription?.trialEndAt;
  if ((status !== 'TRIALING' && status !== 'EXPIRED') || !trialEndAt) {
    return { trialDaysLeft: null, showTrialBanner: false };
  }

  // 만료 후에는 남은 일수가 음수가 되므로 0으로 클램프한다.
  // TrialBanner는 `daysLeft <= 0`을 만료 문구 + error 톤으로 렌더한다.
  const trialDaysLeft = Math.max(0, Math.ceil((new Date(trialEndAt).getTime() - Date.now()) / DAY_MS));

  return {
    trialDaysLeft,
    showTrialBanner: billingEnabled && (status === 'EXPIRED' || trialDaysLeft <= 7),
  };
};

export type BillingExposure = {
  showTrialBanner: boolean;
  trialDaysLeft: number | null;
  tier: 'FREE' | 'PRO';
  subscriptionStatus: string | null;
  subscriptionLoading: boolean;
  isBillingEnabled: boolean;
};

export const useBillingExposure = (
  token: string | null,
  tier: 'FREE' | 'PRO',
  isBetaUser: boolean = false,
): BillingExposure => {
  // Subscription drives the Pro/Trial badge shown globally (incl. home route).
  const { data: subscription, isLoading: subscriptionLoading } = useSubscription(token);

  return useMemo(() => {
    const billingEnabled = isBetaUser;
    const { trialDaysLeft, showTrialBanner } = getTrialExposure(subscription, billingEnabled);

    return {
      showTrialBanner,
      trialDaysLeft,
      tier,
      subscriptionStatus: subscription?.status ?? null,
      subscriptionLoading,
      isBillingEnabled: billingEnabled,
    };
  }, [tier, subscription, subscriptionLoading, isBetaUser]);
};
